<script lang="ts">
	import { onMount } from 'svelte';
	import { AboutIntroSection } from './AboutIntroSection';
	import { ArtistsSection } from './ArtistsSection';
	import { LatestReleasesSection } from './LatestReleasesSection';
	import { NewsletterSection } from './NewsletterSection';
	import { NewsSection } from './NewsSection';
	import { ShopSection } from './ShopSection';
	import { TalksSection } from './TalksSection';
	import { getHeroWithDropButtons } from '#lib/features/hero/utils/getHeroWithDropButtons/getHeroWithDropButtons.js';
	import { getLiveDropInfoContext } from '#lib/features/live-drop-info/LiveDropInfoContext/utils/getLiveDropInfoContext/getLiveDropInfoContext.js';
	import { getLiveSlug } from '#lib/features/live-drop-info/utils/getLiveSlug/getLiveSlug.js';
	import { NavBar } from '#lib/features/navigation/components/NavBar/index.js';
	import { NavBarLogo } from '#lib/features/navigation/components/NavBarLogo/index.js';
	import { PageBody } from '#lib/features/navigation/components/PageBody/index.js';
	import { PrivateSalesBanner } from '#lib/features/private-sales/components/PrivateSalesBanner/index.js';
	import { GlobalSearch } from '#lib/features/search/global/components/GlobalSearch/index.js';
	import { getHomepageJsonLd } from '#lib/features/seo/utils/getHomepageJsonLd/getHomepageJsonLd.js';
	import { getTimeContext } from '#lib/features/time/utils/getTimeContext/getTimeContext.js';
	import { page } from '$app/state';
	import { Container } from '$global/components/Container';
	import { GlobalHomePageHero } from '$global/components/heni-websites-heroes';
	import { SearchPageContainer } from '$global/features/search/components/SearchPageContainer';
	import { createSearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { JsonLd } from '$global/features/seo/components/JsonLd';
	import type { GlobalHomePageData } from '$routes/types';

	let data = $derived(page.data as GlobalHomePageData);
	let hero = $derived(data.hero);

	// Server time rather than the ISR render time: the hero swaps to its
	// inactive buttons once its drop closes, even on a cached page.
	const { updatedTime, fetchTime, interval } = getTimeContext();

	onMount(() => {
		fetchTime();

		return () => {
			if ($interval) {
				clearInterval($interval);
			}

			interval.set(null);
		};
	});

	let heroWithDropButtons = $derived(
		getHeroWithDropButtons(hero, $updatedTime)
	);

	let heroSlug = $derived(hero.drop_config?.drop_page?.slug);

	let { liveDropInfoCtx } = getLiveDropInfoContext();

	let liveSlugs = $derived(liveDropInfoCtx.liveDropInfo?.liveSlugs);

	let isLive = $derived(
		!!heroSlug &&
			!!liveSlugs?.includes(
				getLiveSlug(heroSlug, hero.drop_config?.platform || '')
			)
	);

	let desktopImageStyle = $derived.by(() => {
		const zoom = hero?.desktop_image_zoom || 1;
		const posX = hero?.desktop_image_position_x ?? 50;
		const posY = hero?.desktop_image_position_y ?? 50;
		return `transform: scale(${zoom}); transform-origin: ${posX}% ${posY}%;`;
	});

	let mobileImageStyle = $derived.by(() => {
		const zoom = hero?.mobile_image_zoom || 1;
		const posX = hero?.mobile_image_position_x ?? 50;
		const posY = hero?.mobile_image_position_y ?? 50;
		return `transform: scale(${zoom}); transform-origin: ${posX}% ${posY}%;`;
	});

	const dataCy = 'global-home';

	const combobox = createSearchCombobox();
</script>

<JsonLd data={getHomepageJsonLd()} />

<NavBar dark SearchComponent={GlobalSearch} {combobox} isSearchEnabled={true}>
	{#snippet homeIcon()}
		<NavBarLogo />
	{/snippet}
</NavBar>
<SearchPageContainer {combobox}>
	<PageBody>
		<GlobalHomePageHero
			hero={heroWithDropButtons}
			{isLive}
			alt={hero.surtitle}
			class="mb-8 sm:mb-12"
			{desktopImageStyle}
			{mobileImageStyle}
		/>

		<Container {dataCy}>
			<!-- Tentative "what HENI is" strip — remove this block to drop it. -->
			{#if data.aboutIntro}
				<div class="mb-8 sm:mb-12">
					<AboutIntroSection />
				</div>
			{/if}

			<div class="mb-16">
				<LatestReleasesSection />
			</div>
		</Container>

		<NewsletterSection />

		<Container {dataCy}>
			<div class="mb-16"><ArtistsSection /></div>

			<div class="mb-16"><ShopSection /></div>
		</Container>

		<Container {dataCy}>
			<div class="mb-16 min-w-0">
				<TalksSection />
			</div>

			<div class="mb-16 min-w-0">
				<NewsSection />
			</div>

			<div class="mb-16">
				<PrivateSalesBanner
					imageUrl="/img-svelte/private_sales_banner.png"
					{dataCy}
				/>
			</div>
		</Container>
	</PageBody>
</SearchPageContainer>
